'use client';

import ArticleCard from '@pickleballinc/features/src/hub-cards/ArticleCard';
import SkeletonArticleCard from '@pickleballinc/features/src/hub-cards/SkeletonArticleCard';
import MyModulesHeader from '@pickleballinc/ui/components/my-modules/MyModulesHeader';
import NoDataFound from '@pickleballinc/ui/components/pickleball-app/NoDataFound';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useQuery } from '@tanstack/react-query';

import BlogPagination from '@/blog/components/BlogPagination';
import { PAGE_SIZE } from '@/blog/constants';
import { getArticles } from '@/utils/actions/blog/getArticles';

interface PaginationContentProps {
	categorySlug: string;
	currentPage: string;
}

const PaginationContent = ({ categorySlug, currentPage }: PaginationContentProps) => {
	const { data, isFetching } = useQuery({
		queryKey: ['articles', categorySlug, currentPage],
		queryFn: async () => await getArticles({ slug: categorySlug, page: Number(currentPage) })
	});

	const articles = data?.data?.articles || [];
	const totalPages = data?.data?.totalRecords ? Math.ceil(data.data.totalRecords / PAGE_SIZE) : 1;

	return (
		<>
			<div className="w-full lg:flex-1">
				<div className="flex w-full flex-col gap-2 sm:mx-auto sm:gap-4">
					<MyModulesHeader
						backButtonText="Back to Tournaments"
						text="Pickleball Tournaments Blog"
						description="Stay Informed: Your Comprehensive Source for Pickleball Play Solutions, Results, and Player Updates"
					/>
				</div>
			</div>
			{articles.length === 0 && !isFetching ? (
				<div className="mt-6 flex w-full items-center justify-center sm:mt-8">
					<NoDataFound
						application="tournament"
						title="No articles found"
						subtitle="There are no articles created for Pickleball Tournaments Blog."
						imageURL={`${process.env.NEXT_PUBLIC_IMAGE_CDN}`}
					/>
				</div>
			) : (
				<div>
					<div className="flex flex-col items-center pt-2 sm:pt-12">
						<div className="mb-4 grid w-full max-w-screen-xl grid-cols-2 justify-center gap-4 px-2 sm:grid-cols-3 sm:gap-y-10 sm:px-0">
							{isFetching
								? Array.from({ length: 6 }).map((_, index) => <SkeletonArticleCard key={index} />)
								: articles.map((article) => (
										<CustomLink
											href={`/${categorySlug}/${article.slug}`}
											key={`article-${article.slug}`}
											className="flex justify-center"
										>
											<ArticleCard
												imageUrl={article.imageUrl}
												alt={article.imageAltText}
												title={article.title}
												description={article.description}
												type="paginationContain"
												showDate
												publishedAt={article.publishDateDisplayed}
												isTournaments={true}
											/>
										</CustomLink>
									))}
						</div>
					</div>
					{articles.length > 0 && (
						<div className="flex flex-col items-center">
							<div className="mb-4 flex w-full max-w-screen-xl flex-col">
								<BlogPagination currentPage={currentPage} totalPages={totalPages} categorySlug={categorySlug} />
							</div>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default PaginationContent;
